import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api'
import type {
  Workspace,
  WorkspaceTree,
  WorkspaceMember,
  WorkspaceResourceBinding,
} from '@/types/workspace'

// ─── Workspace list / tree / single ───────────────────────────────────────

export function useWorkspaces(orgId: string) {
  return useQuery<{ workspaces: Workspace[] }>({
    queryKey: ['workspaces', orgId],
    // Trailing slash matters: FastAPI 307-redirects /api/workspaces →
    // /api/workspaces/ with an absolute Location header pointing at the
    // backend's internal hostname (dashboard-api:8100), which the browser
    // can't resolve. Same fix applied in AuthProvider.tsx.
    queryFn: () => apiFetch<{ workspaces: Workspace[] }>(`/api/workspaces/?org_id=${orgId}`),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  })
}

export function useWorkspaceTree(orgId: string) {
  return useQuery<{ workspaces: WorkspaceTree[] }>({
    queryKey: ['workspaces-tree', orgId],
    queryFn: () => apiFetch<{ workspaces: WorkspaceTree[] }>(`/api/workspaces/tree?org_id=${orgId}`),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  })
}

export function useWorkspace(id: string) {
  return useQuery<{ workspace: Workspace }>({
    queryKey: ['workspace', id],
    queryFn: () => apiFetch<{ workspace: Workspace }>(`/api/workspaces/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  })
}

// ─── Create workspace ─────────────────────────────────────────────────────

export function useCreateWorkspace(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: {
      name: string
      slug: string
      parent_id?: string
      description?: string
      settings?: Record<string, unknown>
    }) =>
      apiFetch<{ workspace: Workspace }>('/api/workspaces/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ org_id: orgId, ...params }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspaces', orgId] })
      qc.invalidateQueries({ queryKey: ['workspaces-tree', orgId] })
    },
  })
}

// ─── Update workspace ─────────────────────────────────────────────────────

export function useUpdateWorkspace(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...params
    }: {
      id: string
      name?: string
      slug?: string
      description?: string
      settings?: Record<string, unknown>
      parent_id?: string
    }) =>
      apiFetch<{ workspace: Workspace }>(`/api/workspaces/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['workspaces', orgId] })
      qc.invalidateQueries({ queryKey: ['workspaces-tree', orgId] })
      qc.invalidateQueries({ queryKey: ['workspace', vars.id] })
    },
  })
}

// ─── Delete workspace ─────────────────────────────────────────────────────

export function useDeleteWorkspace(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/workspaces/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspaces', orgId] })
      qc.invalidateQueries({ queryKey: ['workspaces-tree', orgId] })
    },
  })
}

// ─── Members ──────────────────────────────────────────────────────────────

export function useWorkspaceMembers(workspaceId: string) {
  return useQuery<{ members: WorkspaceMember[] }>({
    queryKey: ['workspace-members', workspaceId],
    queryFn: () =>
      apiFetch<{ members: WorkspaceMember[] }>(`/api/workspaces/${workspaceId}/members`),
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
  })
}

export function useAddWorkspaceMember(workspaceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { user_id: string; role?: 'owner' | 'admin' | 'member' | 'viewer' }) =>
      apiFetch<{ member: WorkspaceMember }>(`/api/workspaces/${workspaceId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace-members', workspaceId] }),
  })
}

export function useRemoveWorkspaceMember(workspaceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<void>(`/api/workspaces/${workspaceId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace-members', workspaceId] }),
  })
}

// ─── Resource bindings ────────────────────────────────────────────────────

export function useWorkspaceResources(workspaceId: string) {
  return useQuery<{ resources: WorkspaceResourceBinding[] }>({
    queryKey: ['workspace-resources', workspaceId],
    queryFn: () =>
      apiFetch<{ resources: WorkspaceResourceBinding[] }>(
        `/api/workspaces/${workspaceId}/resources`,
      ),
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
  })
}

export function useBindWorkspaceResource(workspaceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: {
      resource_type: 'project' | 'application' | 'campaign'
      resource_id: string
    }) =>
      apiFetch<{ binding: WorkspaceResourceBinding }>(
        `/api/workspaces/${workspaceId}/resources`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace-resources', workspaceId] }),
  })
}

export function useUnbindWorkspaceResource(workspaceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (bindingId: string) =>
      apiFetch<void>(`/api/workspaces/${workspaceId}/resources/${bindingId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace-resources', workspaceId] }),
  })
}
